import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { StartDate } from '@app/models/payroll/company/start-date.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { UpdateView } from '@app/modules/common/views/update/update.view';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { TypesForm } from '@forms/time-off-v3/components';
import { iconList } from '@time-off-v3/meta/icon-meta';
import { TimeOffService } from '@time-off-v3/services';

@Component({
    selector: 'app-name',
    templateUrl: './types-update.template.html',
    styleUrls: ['./types-update.style.scss'],
})
export class TypesUpdateView extends UpdateView {
    @ViewChild('timeOffTypesForm') timeOffTypesForm: TypesForm;
    timeOffType: TimeOffType;
    iconList: string[] = iconList;
    isLoading = false;
    showTimeOffHoursPayrollModal = false;
    showAcceptingHoursInPayrollModal = false;
    showPayrollIntegrationCannotBeChangedModal = false;
    currentPayrollStartDate: Date;
    hasPendingHoursInPayroll = false;

    constructor(
        private analyticService: AnalyticService,
        private notify: NotifyService,
        private router: Router,
        private route: ActivatedRoute,
        private timeOffService: TimeOffService,
        private auth: AuthService
    ) {
        super();
        this.timeOffType = this.route.snapshot.data.models.get('type');
    }

    async ngOnInit(): Promise<void> {
        if (this.auth.company.isPayrollSyncEnabled) {
            const payrollStartDates = await StartDate.first();
            this.currentPayrollStartDate = payrollStartDates.currentPayrollStartDate;

            const pendingHoursInPayroll = await this.timeOffService.getPendingHoursInPayroll({
                'filters[onOpenPayroll]': 1,
                'filters[status][0]': 'pending',
                'filters[hour_template_type]': 'CompanyAdditionalIncome',
                'filters[hour_template_id]': this.timeOffType.incomeTypeId,
            });

            this.hasPendingHoursInPayroll = pendingHoursInPayroll.length > 0;
        }
    }

    onSaveWithModal(): void {
        if (this.showKeepOrRemovePayrollModal()) {
            this.showTimeOffHoursPayrollModal = true;

            return;
        }

        if (this.showDateAcceptingHoursInPayrollModal()) {
            this.showAcceptingHoursInPayrollModal = true;

            return;
        }

        if (this.showPayrollIntegrationChangeNotAllowedModal()) {
            this.showPayrollIntegrationCannotBeChangedModal = true;

            return;
        }

        this.onSave();
    }

    closePendingHoursInPayrollModal(): void {
        this.showPayrollIntegrationCannotBeChangedModal = false;
        return;
    }

    closeTimeOffHoursPayrollModal(): void {
        this.showTimeOffHoursPayrollModal = false;
        return;
    }

    closeShowAcceptingHoursInPayrollModal(): void {
        this.showAcceptingHoursInPayrollModal = false;
        return;
    }

    async removeTimeOffHoursFromPayroll(): Promise<void> {
        this.showTimeOffHoursPayrollModal = false;

        this.isLoading = true;

        try {
            await this.timeOffService.deletePayrollHours(this.timeOffType.id, this.currentPayrollStartDate);

            this.onSave();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    keepTimeOffHoursInPayroll(): void {
        this.showTimeOffHoursPayrollModal = false;
        this.onSave();
    }

    async confirmShowAcceptingHoursInPayroll(): Promise<void> {
        this.showAcceptingHoursInPayrollModal = false;

        this.isLoading = true;

        try {
            await this.timeOffService.sendPayrollHours(this.timeOffType.id, this.currentPayrollStartDate);

            this.onSave();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onSave(): void {
        this.timeOffTypesForm.submit();

        if (!this.timeOffTypesForm.valid) {
            return;
        }

        this.isLoading = true;

        this.timeOffType
            .save()
            .then(() => {
                this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyEditTimeOffType);
                this.notify.success('time-off-v3.timeOffTypeHasBeenUpdated');
                this.router.navigate(['/time-off/v3/types-and-policies'], { relativeTo: this.route });
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message);
                this.isLoading = false;
            });
    }

    onCancel(): void {
        this.router.navigate(['/time-off/v3/types-and-policies'], { relativeTo: this.route });
    }

    private showKeepOrRemovePayrollModal(): boolean {
        return this.timeOffType.incomeTypeId === null && this.timeOffType.getOriginalAttribute('incomeTypeId') !== null;
    }

    private showDateAcceptingHoursInPayrollModal(): boolean {
        return this.timeOffType.incomeTypeId !== null && this.timeOffType.getOriginalAttribute('incomeTypeId') === null;
    }

    private showPayrollIntegrationChangeNotAllowedModal(): boolean {
        return (
            this.hasPendingHoursInPayroll &&
            this.timeOffType.incomeTypeId !== null &&
            this.timeOffType.getOriginalAttribute('incomeTypeId') !== null &&
            this.timeOffType.incomeTypeId !== this.timeOffType.getOriginalAttribute('incomeTypeId')
        );
    }
}
